/**
 * Hover open/close timing for the preview metrics popup.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import {
  computeHoverPlacement,
  hoverShellStyle,
  type HoverPlacement,
} from './preview-row-hover-placement';

const OPEN_DELAY_MS = 120;
const CLOSE_DELAY_MS = 400;

// Only one preview hover card is open at a time: opening one instantly closes
// the previous, so moving to another row swaps the card instead of showing two
// (the close delay still applies when leaving into empty space or onto the card).
let activeCardClose: (() => void) | null = null;

function pointerOverNode(node: HTMLElement | null, x: number, y: number): boolean {
  if (!node) return false;
  const hit = document.elementFromPoint(x, y);
  return hit instanceof Node && node.contains(hit);
}

export function usePreviewRowHover(hasRow: boolean) {
  const triggerRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const openTimer = useRef<number | null>(null);
  const closeTimer = useRef<number | null>(null);
  const hoverRef = useRef({ trigger: false, popup: false });
  const pointerRef = useRef({ x: 0, y: 0 });
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState<HoverPlacement | null>(null);

  const isHovered = () => hoverRef.current.trigger || hoverRef.current.popup;

  const clearTimers = useCallback(() => {
    if (openTimer.current) window.clearTimeout(openTimer.current);
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    openTimer.current = null;
    closeTimer.current = null;
  }, []);

  // Force this card shut now; held in a ref so the singleton can swap cards.
  const closeNowRef = useRef<() => void>(() => {});
  const closeNow = useCallback(() => {
    clearTimers();
    hoverRef.current = { trigger: false, popup: false };
    setOpen(false);
    setPlacement(null);
    if (activeCardClose === closeNowRef.current) activeCardClose = null;
  }, [clearTimers]);
  closeNowRef.current = closeNow;

  const claim = useCallback(() => {
    if (activeCardClose && activeCardClose !== closeNow) activeCardClose();
    activeCardClose = closeNow;
  }, [closeNow]);

  useEffect(
    () => () => {
      clearTimers();
      if (activeCardClose === closeNowRef.current) activeCardClose = null;
    },
    [clearTimers],
  );

  const measure = useCallback(() => {
    const trigger = triggerRef.current;
    const card = cardRef.current;
    if (!trigger || !card) return;
    setPlacement(computeHoverPlacement(trigger.getBoundingClientRect(), card.getBoundingClientRect()));
  }, []);

  useLayoutEffect(() => {
    if (open) measure();
  }, [open, measure]);

  useEffect(() => {
    if (!open) return;
    // Reposition at most once per frame: a raw scroll listener re-measured
    // (getBoundingClientRect + re-render) on every scroll event, which made the
    // list behind the card scroll in a janky, laggy way.
    let rafId = 0;
    const scheduleMeasure = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        measure();
      });
    };
    const onMove = (event: MouseEvent) => {
      pointerRef.current = { x: event.clientX, y: event.clientY };
    };
    const onChange = (event: Event) => {
      if (event.target instanceof Node && popupRef.current?.contains(event.target)) return;
      scheduleMeasure();
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    window.addEventListener('scroll', onChange, true);
    window.addEventListener('resize', onChange);
    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('scroll', onChange, true);
      window.removeEventListener('resize', onChange);
    };
  }, [open, measure]);

  const scheduleClose = useCallback(() => {
    clearTimers();
    closeTimer.current = window.setTimeout(() => {
      if (isHovered()) return;
      const { x, y } = pointerRef.current;
      if (pointerOverNode(triggerRef.current, x, y)) return;
      if (pointerOverNode(popupRef.current, x, y)) return;
      closeNow();
    }, CLOSE_DELAY_MS);
  }, [clearTimers, closeNow]);

  const handleTriggerEnter = () => {
    if (!hasRow) return;
    hoverRef.current.trigger = true;
    clearTimers();
    openTimer.current = window.setTimeout(() => {
      claim();
      setOpen(true);
    }, OPEN_DELAY_MS);
  };

  const leave = (key: 'trigger' | 'popup') => {
    hoverRef.current[key] = false;
    if (!isHovered()) scheduleClose();
  };

  const handlePopupEnter = () => {
    hoverRef.current.popup = true;
    clearTimers();
    setOpen(true);
  };

  const shellStyle = placement ? hoverShellStyle(placement) : null;

  return {
    triggerRef,
    popupRef,
    cardRef,
    open,
    shellStyle,
    handleTriggerEnter,
    handleTriggerLeave: () => leave('trigger'),
    handlePopupEnter,
    handlePopupLeave: () => leave('popup'),
    handleTriggerBlur: () => leave('trigger'),
  };
}
